import React, { useMemo } from 'react';
import { DREAM_CATEGORIES, getCategoryGrouped } from '../data/dreamsData';

interface CategoryViewProps {
  categorySlug: string;
  onSelectCategory: (categorySlug: string) => void;
  onSelectLetter: (letter: string) => void;
  onSelectDream: (slug: string) => void;
  onNavigateToDictionary: () => void;
  onStartAiInterpretation: (symbolName?: string) => void;
  onBackToHome: () => void;
}

const preventDefault = (e: React.MouseEvent, handler: () => void) => {
  if (!(e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1)) {
    e.preventDefault();
    handler();
  }
};

const CategoryView: React.FC<CategoryViewProps> = ({
  categorySlug,
  onSelectCategory,
  onSelectLetter,
  onSelectDream,
  onNavigateToDictionary,
  onStartAiInterpretation,
  onBackToHome
}) => {
  const category = DREAM_CATEGORIES.find(c => c.slug === categorySlug) || {
    slug: categorySlug,
    name: 'أقسام الأحلام',
    description: 'تفسير الرؤى والأحلام المصنفة',
    icon: '✨'
  };

  const { enhanced: featuredDreams, plain: allDictEntries } = useMemo(() => getCategoryGrouped(categorySlug), [categorySlug]);

  const totalSymbols = featuredDreams.length + allDictEntries.length;

  return (
    <div className="min-h-screen w-full bg-[#1E293B] text-slate-100 py-8 px-4 sm:px-6 lg:px-8" dir="rtl">
      <div className="max-w-5xl mx-auto">
        {/* Navigation */}
        <nav aria-label="مسار التصفح" className="flex items-center gap-2 text-sm text-slate-300 mb-8 pb-4 border-b border-slate-700/60 flex-wrap">
          <a href="/" onClick={(e) => preventDefault(e, onBackToHome)} className="hover:text-emerald-400 transition-colors no-underline">الرئيسية</a>
          <span>/</span>
          <a href="/dictionary" onClick={(e) => preventDefault(e, onNavigateToDictionary)} className="hover:text-emerald-400 transition-colors no-underline">قاموس الأحلام</a>
          <span>/</span>
          <span className="text-emerald-400 font-semibold">{category.name}</span>
          <span className="mr-auto text-slate-400 text-xs">
            إجمالي الرموز: <span className="text-emerald-400 font-bold">{totalSymbols}</span>
          </span>
        </nav>

        {/* Header */}
        <header className="text-center mb-10">
          <span className="text-5xl mb-3 block">{category.icon}</span>
          <h1 className="text-2xl sm:text-4xl font-extrabold text-white mb-3">
            تفسير أحلام {category.name}
          </h1>
          <p className="text-slate-300 text-base max-w-xl mx-auto">
            {category.description} في المنام مستندة إلى مصادر التراث في علم التعبير.
          </p>
        </header>

        {/* Category switcher */}
        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {DREAM_CATEGORIES.map((cat) => (
            <a
              key={cat.slug}
              href={`/category/${cat.slug}`}
              onClick={(e) => preventDefault(e, () => { onSelectCategory(cat.slug); })}
              className={`no-underline px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-1.5 ${
                cat.slug === categorySlug
                  ? 'bg-emerald-600 text-white shadow-md'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
              }`}
            >
              <span>{cat.icon}</span>
              <span>{cat.name}</span>
            </a>
          ))}
        </div>

        {/* Results */}
        {totalSymbols > 0 ? (
          <>
          {/* Featured Enhanced */}
          {featuredDreams.length > 0 && (
            <div className="mb-12">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span>⭐</span>
                <span>الرموز المحسنة والمفصلة لهذا القسم</span>
                <span className="text-sm font-normal text-slate-400">({featuredDreams.length} رمزاً)</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {featuredDreams.map((dream) => (
                  <a
                    key={dream.slug}
                    href={`/dream/${dream.slug}`}
                    onClick={(e) => preventDefault(e, () => onSelectDream(dream.slug))}
                    className="block no-underline bg-slate-800/60 border border-slate-700/60 hover:border-emerald-500 rounded-2xl p-6 transition-all hover:shadow-xl group cursor-pointer flex flex-col justify-between"
                  >
                    <div>
                      <a
                        href={`/letter/${encodeURIComponent(dream.letter)}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          preventDefault(e, () => onSelectLetter(dream.letter));
                        }}
                        className="no-underline text-xs bg-emerald-500/10 text-emerald-400 px-2.5 py-1 rounded-md font-semibold border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors inline-block"
                      >
                        حرف ({dream.letter})
                      </a>
                      <h2 className="text-xl font-bold text-white mt-3 mb-2 group-hover:text-emerald-400 transition-colors">
                        {dream.title}
                      </h2>
                      <p className="text-slate-300 text-sm leading-relaxed mb-4 line-clamp-3">
                        {dream.summary}
                      </p>
                    </div>
                    <div className="pt-4 border-t border-slate-700/50 flex items-center justify-between gap-3 flex-wrap">
                      <span className="text-emerald-400 text-sm font-semibold group-hover:translate-x-[-4px] transition-transform">
                        قراءة التفسير بالكامل ←
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onStartAiInterpretation(dream.name);
                        }}
                        className="text-xs text-slate-200 hover:text-emerald-300 bg-slate-900/80 px-2.5 py-1 rounded-lg border border-slate-700 hover:border-emerald-500 transition-colors"
                      >
                        تفسير مخصص ✨
                      </button>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* All Dictionary Entries for this Category */}
          {allDictEntries.length > 0 && (
            <section className="mb-12">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span>📖</span>
                <span>رموز القسم من قاموس الأحلام</span>
                <span className="text-sm font-normal text-slate-400">
                  ({allDictEntries.length} رمزاً)
                </span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {allDictEntries.map((entry) => (
                  <a
                    key={entry.slug}
                    href={`/dream/${entry.slug}`}
                    onClick={(e) => preventDefault(e, () => onSelectDream(entry.slug))}
                    className="block no-underline bg-slate-800/40 border border-slate-700/50 hover:border-teal-500 rounded-xl p-4 transition-all cursor-pointer group flex flex-col justify-between gap-3"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <a
                          href={`/letter/${encodeURIComponent(entry.letter)}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            preventDefault(e, () => onSelectLetter(entry.letter));
                          }}
                          className="no-underline text-[11px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20 hover:bg-emerald-500/20 hover:text-emerald-300 transition-colors inline-block"
                        >
                          حرف ({entry.letter})
                        </a>
                      </div>
                      <h3 className="font-bold text-white group-hover:text-teal-300 transition-colors">{entry.name}</h3>
                      <p className="text-xs text-slate-400 leading-relaxed mt-1 line-clamp-2">{entry.text.slice(0, 140)}</p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onStartAiInterpretation(entry.name);
                      }}
                      className="text-[11px] text-slate-200 hover:text-emerald-300 bg-slate-900/80 px-2 py-0.5 rounded border border-slate-700 self-start"
                    >
                      تفسير مخصص ✨
                    </button>
                  </a>
                ))}
              </div>
            </section>
          )}
          </>
        ) : (
          <div className="bg-slate-800/40 border border-slate-700 rounded-3xl p-12 text-center max-w-lg mx-auto mb-12">
            <span className="text-5xl mb-4 block">🌙</span>
            <h3 className="text-xl font-bold text-white mb-2">جاري إضافة المزيد من الرموز لهذا القسم</h3>
            <p className="text-slate-300 text-sm mb-6">
              يمكنك تصفح الأقسام الأخرى أو استخدام الذكاء الاصطناعي لتفسير حلمك مباشرة.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              <a
                href="/dictionary"
                onClick={(e) => preventDefault(e, onNavigateToDictionary)}
                className="no-underline px-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl transition-all text-sm"
              >
                تصفح قاموس الأحلام ←
              </a>
              <button
                onClick={() => onStartAiInterpretation()}
                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl transition-all shadow-lg text-sm"
              >
                فسر حلمك بالذكاء الاصطناعي ←
              </button>
            </div>
          </div>
        )}

        {/* Footer Navigation */}
        <div className="flex items-center justify-between pt-6 border-t border-slate-700/60 flex-wrap gap-4">
          <a
            href="/dictionary"
            onClick={(e) => preventDefault(e, onNavigateToDictionary)}
            className="no-underline text-slate-300 hover:text-white text-sm px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 transition-colors"
          >
            ← العودة لقاموس الأحلام
          </a>
          <button
            onClick={() => onStartAiInterpretation()}
            className="text-emerald-400 hover:text-emerald-300 text-sm font-bold px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg hover:bg-emerald-500/20 transition-colors"
          >
            تفسير حلم جديد ✨
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoryView;
